// `@/lib/network` — runtime network selection (devnet vs mainnet).
//
// Single deployment ships both networks side-by-side. Users toggle at
// runtime via the in-app switch (cookie-backed). New visitors land on
// `getDefaultNetwork()` ('devnet' today). Per-network config resolves
// from `NEXT_PUBLIC_*_DEVNET` / `_MAINNET` env vars, with optional
// `_LOCAL` overrides for engineer dev (never deployed to Vercel).
//
// Boundary with `@/lib/seo`: the SEO library ALWAYS pins to mainnet
// (`getNetworkConfig('mainnet')`) regardless of which network the
// user has toggled to. User-toggle is invisible to crawlers. Hard rule
// — see `frontend/.claude/rules/seo.md`.
//
// Public surface:
//   - `getNetworkConfig(network)` — server + client. Source of truth
//     for API URL / RPC URL / program ID / API key / auth callback URL
//     per network. THROWS if the network's required env vars aren't
//     populated; gate with `isNetworkConfigured(network)` first.
//   - `isNetworkConfigured(network)` — true iff every required field
//     for `network` is set. Use before any code path that depends on
//     a network being live (SDK init, SEO fetch).
//   - `getDefaultNetwork()` — what fresh visitors land on. Always
//     `'devnet'` today.
//   - `isToggleEnabled()` — true iff both networks are configured;
//     used to show/hide the toggle UI.
//   - `getServerNetwork()` / `getServerToggleNetwork()` — Server
//     Components / route handlers. Reads cookie, falls back to default.
//   - `useUserNetwork()` — Client Components. Hook returning current
//     network + setter + `toggleEnabled` flag.
//   - Demo-mode helpers (`withDemoMode`, `addDemoModePath`,
//     `isDemoModePath`, `stripDemoModePath`, `DEMO_MODE_EXCEPTIONS`)
//     for the `/demo-mode/` path-prefix marker on devnet share URLs.

export type { NetworkConfig } from './config';
export {
  getDefaultNetwork,
  getNetworkConfig,
  isNetworkConfigured,
  isToggleEnabled,
} from './config';
export {
  addDemoModePath,
  appendDemoModeFlag,
  DEMO_MODE_EXCEPTIONS,
  DEMO_MODE_REWRITE_PATHS,
  DEMO_MODE_SEGMENT,
  isDemoModeAwarePath,
  isDemoModePath,
  isDemoModeRewritePath,
  isDemoModeUrlSearchParams,
  NETWORK_PREFERENCE_COOKIE,
  NETWORK_PREFERENCE_MAX_AGE,
  parseNetworkPreference,
  resolveAuthRedirect,
  shouldDefaultToDemoMode,
  stripDemoModePath,
  stripLeadingLocalePath,
  withDemoMode,
} from './preference';
export type { Network } from './types';
export { isNetwork, isToggleableNetwork, NETWORKS } from './types';
// Server-only helpers (next/headers) — import directly from
// `@/lib/network/server` to avoid pulling `next/headers` into the
// client bundle via the barrel.
//
// Client-only helpers — import from `@/lib/network/client` (hook) or
// `@/lib/network/store` (Zustand store) directly. Same reasoning:
// `'use client'` boundary keeps them out of server bundles.
